import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, AlertCircle, Loader2, Copy, ExternalLink, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SMARTLINK_PLATFORMS, SMARTLINK_SOCIALS } from "@/lib/smartlinkPlatforms";

const API = process.env.REACT_APP_BACKEND_URL;
const QUICK_PLATFORM_KEYS = ["spotify", "apple_music", "youtube_music", "deezer", "tidal", "amazon_music", "soundcloud", "tiktok"];
const MIN_RECOMMENDED_PLATFORMS = 5;

const emptyForm = {
  upc: "", title: "", artist: "", cover_url: "", release_date: "", status: "pre_release",
  platform_links: {}, social_links: {}, artist_note: "",
};

export default function CreateSmartlink() {
  const { t } = useLanguage();
  const cs = t.createSmartlink;
  const [form, setForm] = useState(emptyForm);
  const [looking, setLooking] = useState(false);
  const [lookupMsg, setLookupMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedManage, setCopiedManage] = useState(false);

  const publicUrl = created ? `${window.location.origin}/smartlink/${created.slug}` : "";
  const manageUrl = created ? `${publicUrl}?manage=1` : "";
  const activeLinksCount = Object.values(form.platform_links).filter((v) => v && v.trim()).length;

  const handleLookup = async () => {
    if (!form.upc.trim()) return;
    setLooking(true);
    setLookupMsg("");
    try {
      const res = await axios.get(`${API}/api/smartlinks/lookup`, { params: { upc: form.upc.trim() } });
      if (res.data.found) {
        setForm((f) => ({
          ...f,
          title: res.data.title || f.title,
          artist: res.data.artist || f.artist,
          cover_url: res.data.cover_url || f.cover_url,
          release_date: res.data.release_date || f.release_date,
          platform_links: { ...res.data.platform_links, ...f.platform_links },
        }));
        setLookupMsg(cs.lookupFound);
      } else {
        setLookupMsg(cs.lookupNotFound);
      }
    } catch {
      setLookupMsg(cs.lookupError);
    } finally {
      setLooking(false);
    }
  };

  const handlePlatformUrlChange = (key, value) => {
    setForm((f) => ({ ...f, platform_links: { ...f.platform_links, [key]: value } }));
  };

  const handleSocialUrlChange = (key, value) => {
    setForm((f) => ({ ...f, social_links: { ...f.social_links, [key]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.upc.trim() || !form.title.trim() || !form.artist.trim() || !form.cover_url.trim()) {
      setError(cs.requiredFieldsError);
      return;
    }
    setSubmitting(true);
    try {
      const cleanedLinks = Object.fromEntries(Object.entries(form.platform_links).filter(([, v]) => v && v.trim()));
      const cleanedSocials = Object.fromEntries(Object.entries(form.social_links).filter(([, v]) => v && v.trim()));
      const res = await axios.post(`${API}/api/smartlinks`, {
        upc: form.upc.trim(), title: form.title, artist: form.artist, cover_url: form.cover_url,
        release_date: form.release_date || null, status: form.status,
        platform_links: cleanedLinks, social_links: cleanedSocials, artist_note: form.artist_note.trim() || null,
      });
      setCreated(res.data);
    } catch (err) {
      if (err.response?.status === 429) setError(cs.rateLimitError);
      else setError(cs.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* no-op */
    }
  };

  const handleCopyManage = async () => {
    try {
      await navigator.clipboard.writeText(manageUrl);
      setCopiedManage(true);
      setTimeout(() => setCopiedManage(false), 2000);
    } catch {
      /* no-op */
    }
  };

  const handleCreateAnother = () => {
    setForm(emptyForm);
    setCreated(null);
    setError("");
    setLookupMsg("");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="create-smartlink-page">
      <div className="max-w-2xl mx-auto px-6 py-16 sm:py-24">
        <Link to="/" data-testid="create-smartlink-back-home" className="inline-flex items-center gap-2 text-neutral-500 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Doble R Records
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">{cs.pageTitle}</h1>
        <p className="text-neutral-400 text-base mb-10 max-w-xl">{cs.pageSubtitle}</p>

        {created ? (
          <div data-testid="create-smartlink-success" className="flex flex-col gap-6">
            <div className="rounded-2xl border border-brand-blue/30 bg-brand-blue/[0.06] p-6 flex flex-col gap-4">
              <div className="flex items-center gap-2 text-brand-blue">
                <CheckCircle2 className="w-5 h-5" />
                <p className="font-bold text-white text-lg">{cs.successTitle}</p>
              </div>
              <p className="text-neutral-400 text-sm">{cs.successSubtitle}</p>
              <div>
                <p className="text-neutral-500 text-xs uppercase tracking-wide font-semibold mb-2">{cs.yourLinkLabel}</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={publicUrl} data-testid="create-smartlink-result-url" className="bg-white/[0.03] border-white/10 text-white text-sm" />
                  <button type="button" onClick={handleCopy} data-testid="create-smartlink-copy-button" className="h-10 px-4 rounded-lg border border-white/15 text-sm font-semibold hover:bg-white/[0.06] transition-colors shrink-0 flex items-center gap-1.5">
                    <Copy className="w-4 h-4" /> {copied ? cs.copiedLabel : cs.copyButton}
                  </button>
                </div>
              </div>
              <a
                href={`/smartlink/${created.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="create-smartlink-view-button"
                className="w-full bg-brand-red text-white font-bold text-sm py-3.5 rounded-full flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_8px_24px_-6px_rgba(255,36,54,0.5)] transition-all duration-300"
              >
                <ExternalLink className="w-4 h-4" /> {cs.viewButton}
              </a>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex flex-col gap-3">
              <p className="text-neutral-300 text-xs uppercase tracking-wide font-semibold">{cs.manageLinkLabel}</p>
              <p className="text-neutral-500 text-xs leading-relaxed">{cs.manageLinkHint}</p>
              <div className="flex items-center gap-2">
                <Input readOnly value={manageUrl} data-testid="create-smartlink-manage-url" className="bg-white/[0.03] border-white/10 text-white text-sm" />
                <button type="button" onClick={handleCopyManage} data-testid="create-smartlink-copy-manage-button" className="h-10 px-4 rounded-lg border border-white/15 text-sm font-semibold hover:bg-white/[0.06] transition-colors shrink-0 flex items-center gap-1.5">
                  <Copy className="w-4 h-4" /> {copiedManage ? cs.copiedLabel : cs.copyManageButton}
                </button>
              </div>
            </div>
            <button type="button" onClick={handleCreateAnother} data-testid="create-smartlink-another-button" className="text-sm font-semibold text-white px-6 py-3 rounded-full border border-white/15 hover:bg-white/[0.06] transition-all duration-300 self-start">
              {cs.createAnotherButton}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="create-smartlink-form" className="flex flex-col gap-6">
            <div className="flex items-end gap-2">
              <div className="flex-1 flex flex-col gap-2">
                <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.upcLabel}</Label>
                <Input
                  data-testid="create-smartlink-upc-input"
                  value={form.upc}
                  onChange={(e) => setForm((f) => ({ ...f, upc: e.target.value }))}
                  placeholder={cs.upcPlaceholder}
                  className="bg-white/[0.03] border-white/10 text-white h-12"
                />
              </div>
              <button
                type="button"
                onClick={handleLookup}
                disabled={looking || !form.upc.trim()}
                data-testid="create-smartlink-lookup-button"
                className="h-12 px-5 rounded-lg border border-white/15 text-sm font-semibold hover:bg-white/[0.06] transition-colors flex items-center gap-2 disabled:opacity-40"
              >
                {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {cs.lookupButton}
              </button>
            </div>
            {lookupMsg && <p className="text-neutral-400 text-xs" data-testid="create-smartlink-lookup-message">{lookupMsg}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 flex flex-col gap-2">
                <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.titleLabel}</Label>
                <Input data-testid="create-smartlink-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white h-11" />
              </div>
              <div className="sm:col-span-2 flex flex-col gap-2">
                <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.artistLabel}</Label>
                <Input data-testid="create-smartlink-artist-input" value={form.artist} onChange={(e) => setForm((f) => ({ ...f, artist: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white h-11" />
              </div>
              <div className="sm:col-span-2 flex flex-col gap-2">
                <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.coverLabel}</Label>
                <Input data-testid="create-smartlink-cover-input" value={form.cover_url} onChange={(e) => setForm((f) => ({ ...f, cover_url: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white h-11" />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.releaseDateLabel}</Label>
                <Input type="date" data-testid="create-smartlink-date-input" value={form.release_date} onChange={(e) => setForm((f) => ({ ...f, release_date: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white h-11" />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.statusLabel}</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="create-smartlink-status-select" className="bg-white/[0.03] border-white/10 text-white h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_release">{cs.statusPreRelease}</SelectItem>
                    <SelectItem value="released">{cs.statusReleased}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <p className="text-neutral-300 text-xs font-semibold tracking-wide uppercase mb-1">{cs.platformLinksLabel}</p>
              <p className="text-neutral-500 text-xs mb-3">{cs.platformLinksHint}</p>
              {activeLinksCount < MIN_RECOMMENDED_PLATFORMS && (
                <div data-testid="create-smartlink-min-platforms-warning" className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-3 mb-3">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-amber-200 text-xs leading-relaxed">
                    {cs.minPlatformsWarning.replace("{count}", activeLinksCount)}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SMARTLINK_PLATFORMS.filter((p) => QUICK_PLATFORM_KEYS.includes(p.key)).map((p) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.key} className="flex items-center gap-2">
                      <Icon className="w-4 h-4 shrink-0" style={{ color: p.color }} />
                      <Input
                        data-testid={`create-smartlink-platform-url-${p.key}`}
                        value={form.platform_links[p.key] || ""}
                        onChange={(e) => handlePlatformUrlChange(p.key, e.target.value)}
                        placeholder={p.name}
                        className="bg-white/[0.03] border-white/10 text-white h-10 text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-neutral-300 text-xs font-semibold tracking-wide uppercase mb-3">{cs.socialLinksLabel}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SMARTLINK_SOCIALS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex items-center gap-2">
                      <Icon className="w-4 h-4 shrink-0" style={{ color: s.color }} />
                      <Input
                        data-testid={`create-smartlink-social-url-${s.key}`}
                        value={form.social_links[s.key] || ""}
                        onChange={(e) => handleSocialUrlChange(s.key, e.target.value)}
                        placeholder={s.name}
                        className="bg-white/[0.03] border-white/10 text-white h-10 text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{cs.artistNoteLabel}</Label>
              <Textarea
                data-testid="create-smartlink-note-input"
                value={form.artist_note}
                onChange={(e) => setForm((f) => ({ ...f, artist_note: e.target.value }))}
                placeholder={cs.artistNotePlaceholder}
                rows={3}
                className="bg-white/[0.03] border-white/10 text-white"
              />
            </div>

            {error && (
              <div data-testid="create-smartlink-error" className="flex items-start gap-2.5 rounded-xl border border-brand-red/30 bg-brand-red/[0.06] p-3">
                <AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                <p className="text-neutral-300 text-xs leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              data-testid="create-smartlink-submit-button"
              disabled={submitting}
              className="w-full bg-brand-red text-white font-bold text-sm py-3.5 rounded-full flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_8px_24px_-6px_rgba(255,36,54,0.5)] transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} {submitting ? cs.submitting : cs.submitButton}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
